import { useStore } from '@nanostores/react';
import { AnimatePresence, motion } from 'framer-motion';
import { atom } from 'nanostores';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import * as styles from './index.css';

const SIDEBAR_WIDTH = 370;
const SIDEBAR_ANIMATION_DURATION = 150;

const targetId = 'sidebar-portal';
const activeId = atom<string | null>(null);
const closingId = atom<string | null>(null);

export function open(id: string) {
  activeId.set(id);
  closingId.set(null);
}

export async function close() {
  closingId.set(activeId.get());
  activeId.set(null);
  await new Promise((resolve) => setTimeout(resolve, SIDEBAR_ANIMATION_DURATION));
  closingId.set(null);
}

export function toggle(id: string) {
  if (activeId.get() === id) {
    close();
  } else {
    open(id);
  }
}

// expose the sidebar functions to the window object
// so that the ember ui can hook into it and open/close the sidebar
// to prevent having two sidebars open at the same time
Object.assign(window, {
  __hydra_sidebar__: {
    open,
    close,
    subscribe: activeId.subscribe,
  } satisfies Window['__hydra_sidebar__'],
});

/**
 * Renders the sidebar root element.
 * This is the element that will be used as the target for the sidebar portal.
 */
export function SidebarRoot() {
  const _activeSidebarId = useStore(activeId);
  const isOpen = _activeSidebarId !== null;
  const [display, setDisplay] = useState<'block' | 'none'>('none');

  return (
    <AnimatePresence>
      <motion.div
        animate={{
          width: isOpen ? SIDEBAR_WIDTH : 0,
          display,
        }}
        initial={{ width: 0 }}
        // putting 0 causes a slight scroll on the html element
        // when the sidebar is closed, so we use display: none instead
        // and set the width to 0
        onAnimationStart={() => {
          if (isOpen) {
            setDisplay('block');
          }
        }}
        onAnimationComplete={() => {
          if (!isOpen) {
            setDisplay('none');
          }
        }}
        exit={{
          display: 'none',
        }}
        transition={{ duration: SIDEBAR_ANIMATION_DURATION / 1000, ease: 'easeInOut' }}
        style={{
          zIndex: 40, // pluma navbar z-index
        }}
        className={styles.sidebarRoot}
        id={targetId}
      />
    </AnimatePresence>
  );
}

export function Sidebar({ id, children }: { id: string; children: React.ReactNode }) {
  const _activeSidebarId = useStore(activeId);
  const _closingSidebarId = useStore(closingId);
  const sidebarTarget = document.getElementById(targetId);

  if (_activeSidebarId !== id && _closingSidebarId !== id) {
    return null;
  }

  if (sidebarTarget == null) {
    return null;
  }

  return <>{createPortal(<div className={styles.sidebar}>{children}</div>, sidebarTarget)}</>;
}

export function useSidebarActive(id: string) {
  const active = useStore(activeId) === id;
  const closing = useStore(closingId) === id;

  return active || closing;
}
